import { ProjectMetadata } from "@api/apiClient";
import i18n from "@i18n/i18n";
import InfoIcon from '@mui/icons-material/Info';
import { Box, Card, CardContent, CardHeader, Grid, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import TextField from "@mui/material/TextField";
import { ChangeEvent } from "react";

interface AdditionalMetadataFormProps {
  metadata: ProjectMetadata;
  onChange: (name: string, value: any) => void;
}

export const ProjectMetadataForm = ({ metadata, onChange }: AdditionalMetadataFormProps) => {
  const theme = useTheme();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    const numValue = event.target.type === 'number' ? Number(value) : value;
    onChange(name, numValue);
  };

  return (
    <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
      <CardHeader 
        title={<Typography variant="h6" fontWeight="bold">{i18n.t("dashboard.title.additional-metadata")}</Typography>} 
        elevation={3} 
      />
      <CardContent sx={{ display: "flex", alignItems: "center", borderTop: 1, borderColor: theme.palette.divider }}>
        <Grid container spacing={2} sx={{ width: "100%" }}>
          <Grid size={12}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label={i18n.t("dashboard.title.total-floor-area")}
                  name="totalFloorArea"
                  type="number"
                  value={metadata.totalFloorArea || ''}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    inputProps: { 
                      min: 0,
                      step: "any" // Allow decimal values
                    }
                  }}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label={i18n.t("dashboard.title.service-life")}
                  name="serviceLife"
                  type="number"
                  value={metadata.serviceLife || ''}
                  onChange={handleInputChange}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    inputProps: { 
                      min: 0,
                      step: 1 // Only integer values
                    }
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {i18n.t("dashboard.title.goal")}
                    </Typography>
                    <Tooltip title={i18n.t("dashboard.tooltip.goal")} placement="right">
                      <IconButton size="small" sx={{ padding: 0.5 }}>
                        <InfoIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField
                    name="goal"
                    value={metadata.goal || ''}
                    onChange={handleInputChange}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Box>
              </Grid>
              <Grid size={12}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {i18n.t("dashboard.title.scope")}
                    </Typography>
                    <Tooltip title={i18n.t("dashboard.tooltip.scope")} placement="right">
                      <IconButton size="small" sx={{ padding: 0.5 }}>
                        <InfoIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField
                    name="scope"
                    value={metadata.scope || ''}
                    onChange={handleInputChange}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
